import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/cn'
import { Button } from '../Button/Button'
import s from './Pagination.module.css'

/**
 * Navigation de page d'une liste servie par le serveur (D24).
 *
 * ⚠️ Elle ne connaît **ni** le nombre de pages **ni** la page courante : elle reçoit la tranche
 * et l'effectif de l'ensemble filtré, et en dérive tout le reste. Pas de second état à tenir en
 * accord avec l'URL — l'URL porte la tranche, ce composant l'affiche.
 */
export interface PaginationProps {
  /** Le décalage de la tranche affichée, en lignes — `offset` de la requête. */
  offset: number
  /** La taille de page demandée — `limit` de la requête. */
  pageSize: number
  /** L'effectif de l'ensemble filtré, compté par le serveur. */
  total: number
  onOffsetChange: (offset: number) => void
  /** Le nom des lignes comptées, au pluriel — « demandes », « contrôles ». */
  unit: string
  className?: string
}

export function Pagination({
  offset,
  pageSize,
  total,
  onOffsetChange,
  unit,
  className,
}: PaginationProps) {
  // Une liste vide n'a pas de tranche à annoncer : `1–0 sur 0` se lit comme une panne. L'état
  // vide, lui, est déjà dit par l'écran (`EmptyState`).
  //
  // ⚠️ `offset >= total` est le même cas, décalé : une URL partagée ou mise en favori dont les
  // filtres ne renvoient plus assez de lignes (`?offset=200` sur 10 résultats) donnerait sinon une
  // tranche à l'envers (« 201–10 sur 10 »), affichée à côté de l'état vide de l'écran.
  if (total === 0 || offset >= total) return null

  const first = offset + 1
  const last = Math.min(offset + pageSize, total)
  const hasPrevious = offset > 0
  const hasNext = last < total

  return (
    <nav className={cn(s.root, className)} aria-label={`Pagination des ${unit}`}>
      <p className={s.range}>
        <strong>
          {first}–{last}
        </strong>{' '}
        sur {total} {unit}
      </p>

      <div className={s.controls}>
        <Button
          size="sm"
          // ⚠️ `Math.max(0, …)` et non `offset - pageSize` : une taille de page changée pendant
          // qu'on est en page 3 rendrait un décalage négatif, que le serveur ramènerait à zéro
          // sans le dire. Le borner ici garde l'URL honnête sur ce qui est affiché.
          onClick={() => onOffsetChange(Math.max(0, offset - pageSize))}
          disabled={!hasPrevious}
        >
          <ChevronLeft size={16} aria-hidden /> Précédent
        </Button>
        <Button size="sm" onClick={() => onOffsetChange(offset + pageSize)} disabled={!hasNext}>
          Suivant <ChevronRight size={16} aria-hidden />
        </Button>
      </div>
    </nav>
  )
}
